import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import BlockoutViewer from './BlockoutViewer';
import Lightbox from '@/components/Lightbox';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const STAGES = [
  { key: 'reference', label: 'Références' },
  { key: 'candidate', label: 'Candidates' },
  { key: 'dataset', label: 'Dataset' },
];
const ROLES = ['structure', 'view', 'mood'];

const STAGE_KEYS = STAGES.map((s) => s.key);

// Repli du lieu ouvert, même logique que l'onglet dans AdminPage : l'URL est la
// source première, la mémoire locale rattrape les cas où elle arrive nue.
const LAST_ENV_KEY = 'voss.admin.lastEnv';
const readLastEnv = () => {
  try { return window.localStorage.getItem(LAST_ENV_KEY) || null; } catch { return null; }
};
const rememberLastEnv = (id) => {
  try { window.localStorage.setItem(LAST_ENV_KEY, id); } catch { /* sans conséquence */ }
};
const forgetLastEnv = () => {
  try { window.localStorage.removeItem(LAST_ENV_KEY); } catch { /* sans conséquence */ }
};

const EnvironmentPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [envs, setEnvs] = useState([]);
  const [current, setCurrent] = useState(null);   // environnement ouvert
  const [planAsset, setPlanAsset] = useState(null); // asset "plan" du lieu, indépendant du set affiché
  // Ouverte par défaut. Elle était repliée pour ne pas créer de contexte WebGL
  // à chaque ouverture d'un lieu, mais le composant libère proprement le sien au
  // démontage — et repliée, la vue était introuvable (« je ne vois pas de plan
  // 3D »). La découvrabilité l'emporte.
  const [show3d, setShow3d] = useState(true);
  const [assets, setAssets] = useState([]);
  const stageParam = searchParams.get('stage');
  const stage = STAGE_KEYS.includes(stageParam) ? stageParam : 'reference';
  const setStage = (next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('stage', next);
      return p;
    }, { replace: true });
  };
  const [uploadRole, setUploadRole] = useState('structure');
  const [condScale, setCondScale] = useState(0.6);
  const [generating, setGenerating] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const generatingRef = useRef(false);
  const fileInput = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchEnvs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/environments`, { headers: authHeaders(), withCredentials: true });
      setEnvs(r.data);
    } catch (e) { toast.error('Impossible de charger les environnements'); }
  }, []);

  const fetchAssets = useCallback(async (envId, st) => {
    try {
      const r = await axios.get(`${API}/admin/environments/${envId}/assets?env_stage=${st}`,
        { headers: authHeaders(), withCredentials: true });
      setAssets(r.data);
    } catch (e) { toast.error('Impossible de charger les photos'); }
  }, []);

  // Recharge le détail du lieu ouvert (compteurs d'onglets à jour) + son plan,
  // indépendamment du set de photos actuellement affiché.
  // opts.silent : pas de toast d'erreur — utilisé pour le chargement depuis l'URL,
  // où un identifiant invalide doit retomber sur la liste sans cascade de toasts.
  const refreshCurrent = useCallback(async (envId, opts = {}) => {
    try {
      const r = await axios.get(`${API}/admin/environments/${envId}`, { headers: authHeaders(), withCredentials: true });
      setCurrent(r.data);
      if (r.data.plan_asset_id) {
        try {
          const rp = await axios.get(`${API}/admin/assets/${r.data.plan_asset_id}`,
            { headers: authHeaders(), withCredentials: true });
          setPlanAsset(rp.data);
        } catch (e) { setPlanAsset(null); }
      } else {
        setPlanAsset(null);
      }
      return true;
    } catch (e) {
      if (!opts.silent) toast.error('Environnement introuvable');
      return false;
    }
  }, []);

  useEffect(() => { fetchEnvs(); }, [fetchEnvs]);
  useEffect(() => { if (current) fetchAssets(current.id, stage); }, [current, stage, fetchAssets]);

  // Au montage, si l'URL porte ?env=<id> : recharge directement ce lieu.
  // Identifiant invalide → repli silencieux sur la liste, sans toast.
  useEffect(() => {
    // Même repli que pour l'onglet : l'URL d'abord, la mémoire locale ensuite.
    // Sans elle, retrouver l'onglet Environnement sans retrouver le lieu ouvert
    // ne règle qu'à moitié le problème.
    const envId = searchParams.get('env') || readLastEnv();
    if (!envId) return;
    let active = true;
    (async () => {
      const ok = await refreshCurrent(envId, { silent: true });
      if (!active) return;
      if (ok) {
        // remet l'URL en cohérence quand le lieu venait de la mémoire locale
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          if (!p.get('env')) p.set('env', envId);
          return p;
        }, { replace: true });
        return;
      }
      forgetLastEnv();   // lieu disparu : ne pas le reproposer indéfiniment
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('env');
        p.delete('stage');
        return p;
      }, { replace: true });
    })();
    return () => { active = false; };
    // Ne s'exécute qu'au montage — lit l'URL initiale.
  }, []);

  const openEnv = async (id) => {
    const ok = await refreshCurrent(id);
    if (!ok) return;
    rememberLastEnv(id);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('env', id);
      p.set('stage', 'reference');
      return p;
    }, { replace: true });
  };

  const patchAsset = async (id, patch, msg) => {
    try {
      await axios.patch(`${API}/admin/assets/${id}`, patch, { headers: authHeaders(), withCredentials: true });
      toast.success(msg);
      fetchAssets(current.id, stage);
      fetchEnvs();
      refreshCurrent(current.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la mise à jour'); }
  };

  const removeFromEnv = (id) => {
    patchAsset(id, { environment_id: null, env_role: null, env_stage: null }, 'Retirée du lieu');
  };

  // Génère le décor VIDE : le conditionnement est la carte de profondeur, et
  // le LoRA d'Elara est explicitement écarté côté serveur (include_character).
  const generateDecor = async (codes) => {
    if (!current) return;
    if (codes.length > 1 &&
        !window.confirm(`Lancer ${codes.length} générations ? Chacune est facturée.`)) {
      return;
    }
    // Garde par ref : setGenerating n'est pas synchrone, deux invocations du
    // même tick liraient toutes deux generating === false. L'action est payée.
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/environments/${current.id}/generate-decor`,
        { camera_codes: codes, variants: 1, conditioning_scale: condScale, aspect: '1:1' },
        { headers: authHeaders(), withCredentials: true });
      const n = (r.data.asset_ids || []).length;
      toast.success(`${n} génération(s) lancée(s) — onglet Candidates`);
      await refreshCurrent(current.id);
      if (stage === 'candidate') await fetchAssets(current.id, 'candidate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Génération impossible');
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const rejectAsset = async (id) => {
    try {
      await axios.post(`${API}/admin/assets/${id}/review`, { decision: 'rejected' },
        { headers: authHeaders(), withCredentials: true });
      toast.success('Photo rejetée');
      fetchAssets(current.id, stage);
      fetchEnvs();
      refreshCurrent(current.id);
    } catch (e) { toast.error('Échec du rejet'); }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !current) return;
    const form = new FormData();
    form.append('file', file);
    form.append('folder', 'uploads');
    form.append('environment_id', current.id);
    form.append('env_role', uploadRole);
    form.append('env_stage', 'reference');
    try {
      await axios.post(`${API}/admin/assets/upload`, form, { headers: authHeaders(), withCredentials: true });
      toast.success('Photo ajoutée aux références');
      setStage('reference');
      fetchAssets(current.id, 'reference');
      fetchEnvs();
      refreshCurrent(current.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Échec de l\'upload'); }
    finally { if (fileInput.current) fileInput.current.value = ''; }
  };

  if (!current) {
    return (
      <div data-testid="env-list">
        <p className="form-hint" style={{ marginBottom: '1rem' }}>
          Les lieux d'Elara. Ouvre un lieu pour trier ses photos et constituer son dataset.
        </p>
        <div className="media-grid">
          {envs.map((e) => (
            <button key={e.id} type="button" className="env-card" onClick={() => openEnv(e.id)}
              data-testid={`env-card-${e.slug}`}>
              <span className="env-card-name">{e.name}</span>
              <span className="env-card-counts">
                {e.counts.reference} réf · {e.counts.candidate} cand · {e.counts.dataset} dataset
              </span>
            </button>
          ))}
          {envs.length === 0 && <p className="form-hint">Aucun lieu enregistré.</p>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="env-detail">
      <Lightbox src={lightbox?.src || ''} alt={lightbox?.alt} onClose={() => setLightbox(null)} />

      <div className="env-head">
        <button type="button" className="btn btn-outline" onClick={() => {
          setCurrent(null);
          forgetLastEnv();   // choix explicite de quitter : ne pas y revenir seul
          setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('env');
            p.delete('stage');
            return p;
          }, { replace: true });
        }} data-testid="env-back">
          ← Lieux
        </button>
        <h2 className="admin-title" style={{ marginBottom: 0 }}>{current.name}</h2>
      </div>

      <p className="env-identity">{current.identity}</p>

      {current.style_target && (
        <div className="env-style" data-testid="env-style-target">
          <div className="env-facts-title">Style cible & interdits</div>
          <p className="env-style-text">{current.style_target}</p>
        </div>
      )}

      {current.canon_doc && (
        <p className="form-hint env-canon-doc" data-testid="env-canon-doc">Fiche canon : {current.canon_doc}</p>
      )}

      {planAsset && (
        <div className="env-plan">
          <button
            type="button"
            className="img-button"
            onClick={() => setLightbox({ src: `${BACKEND}${planAsset.media_url}`, alt: 'Plan du lieu' })}
            aria-label="Agrandir le plan du lieu"
          >
            <img src={`${BACKEND}${planAsset.media_url}`} alt="Plan du lieu" />
          </button>
          <span className="form-hint">Plan — référence de structure</span>
        </div>
      )}

      <div className="env-facts">
        <div>
          <div className="env-facts-title">Pièces</div>
          <ul>{(current.rooms || []).map((r) => (
            <li key={r.key}>{r.label} <span className="form-hint">{r.dims} · {r.position}</span></li>))}</ul>
        </div>
        <div>
          <div className="env-facts-title">Adjacences</div>
          <ul>{(current.adjacencies || []).map((a, i) => (
            <li key={i}>{a.from} ⇄ {a.to} <span className="form-hint">{a.note}</span></li>))}</ul>
        </div>
      </div>

      <div className="blk-section">
        <div className="blk-section-head">
          <div className="env-facts-title" style={{ marginBottom: 0 }}>Blockout 3D</div>
          <button type="button" className="btn btn-outline" data-testid="env-3d-toggle"
            onClick={() => setShow3d((v) => !v)}>
            {show3d ? 'Replier la vue 3D' : 'Ouvrir la vue 3D'}
          </button>
        </div>
        {show3d && <BlockoutViewer layout={current.layout_3d} />}
      </div>

      <div className="media-picker-folders" style={{ marginTop: '1.5rem' }}>
        {STAGES.map((s) => (
          <button key={s.key} type="button" className={`media-folder-tab${stage === s.key ? ' active' : ''}`}
            onClick={() => setStage(s.key)} data-testid={`env-stage-${s.key}`}>
            {s.label} ({current.counts ? current.counts[s.key] : 0})
          </button>
        ))}
      </div>

      <div className="env-generate">
        <label className="form-hint" htmlFor="env-cond-scale">
          Intensité du conditionnement : {condScale.toFixed(2)}
        </label>
        <input id="env-cond-scale" type="range" min="0.1" max="1" step="0.05"
          value={condScale} onChange={(ev) => setCondScale(parseFloat(ev.target.value))}
          data-testid="env-cond-scale" />
        <span className="form-hint">
          Trop haut : l'image devient un rendu 3D. Trop bas : la géométrie dérive.
        </span>
        <button type="button" className="btn btn-primary" disabled={generating}
          data-testid="env-generate-all"
          onClick={() => generateDecor((current.cameras || []).map((c) => c.code))}>
          {generating ? 'Génération…' : 'Générer les 8 décors'}
        </button>
      </div>

      <div className="env-upload">
        <select className="form-input" style={{ maxWidth: 180 }} value={uploadRole}
          onChange={(ev) => setUploadRole(ev.target.value)} data-testid="env-upload-role">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.webp,.mp4"
          onChange={doUpload} data-testid="env-upload-input" />
      </div>

      <div className="media-grid" data-testid="env-assets">
        {assets.map((a) => (
          <div key={a.id} className="env-tile" data-testid={`env-asset-${a.id}`}>
            <div className="env-thumb">
              {a.media_url ? (
                <button
                  type="button"
                  className="img-button"
                  onClick={() => setLightbox({ src: `${BACKEND}${a.media_url}`, alt: a.env_role || '' })}
                  aria-label="Agrandir"
                >
                  <img src={`${BACKEND}${a.media_url}`} alt={a.env_role || ''} loading="lazy" />
                </button>
              ) : <div className="form-hint">—</div>}
            </div>
            <div className="env-tile-role">
              {ROLES.map((r) => (
                <button key={r} type="button"
                  className={`env-role-btn${a.env_role === r ? ' active' : ''}`}
                  onClick={() => patchAsset(a.id, { env_role: r }, `Rôle : ${r}`)}>{r}</button>
              ))}
            </div>
            <div className="env-tile-actions">
              {stage !== 'dataset' && (
                <button type="button" className="btn btn-primary"
                  onClick={() => patchAsset(a.id, { env_stage: 'dataset' }, 'Ajoutée au dataset')}>
                  → dataset
                </button>
              )}
              {stage !== 'reference' && (
                <button type="button" className="btn btn-secondary"
                  onClick={() => patchAsset(a.id, { env_stage: 'reference' }, 'Passée en référence')}>
                  → réf.
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => removeFromEnv(a.id)}>Retirer</button>
              <button type="button" className="btn btn-outline" onClick={() => rejectAsset(a.id)}>Rejeter</button>
              {a.env_role === 'structure' && a.params?.camera_code
                && (a.params?.aspect || '1:1') === '1:1' && (
                <button type="button" className="btn btn-secondary" disabled={generating}
                  data-testid={`env-generate-${a.params.camera_code}`}
                  onClick={() => generateDecor([a.params.camera_code])}>
                  Générer le décor
                </button>
              )}
            </div>
          </div>
        ))}
        {assets.length === 0 && <p className="form-hint">Rien dans ce set.</p>}
      </div>
    </div>
  );
};

export default EnvironmentPanel;
